import { useNavigate, useParams } from '@solidjs/router';
import { createMemo } from 'solid-js';
import { OnboardingV3Routes, onboardingV3StepsDict } from '~/components/Onboarding/V3/config';
import { createMagicDoorContext } from '~/contexts/utils';
import { OnboardingStatus } from '~/utils/constant';
import { useOnboarding } from './OnboardingContext';
import type { OnboardingV3Status } from '~/utils/constant';

export const [OnboardingV3Provider, useOnboardingV3] = createMagicDoorContext('OnboardingV3', () => {
  const routes = onboardingV3StepsDict.getOptions().map((route) => route.meta?.path);
  const { companies, updateOnboardingStep, completeOnboarding } = useOnboarding();

  const totalSteps = routes.length;
  const navigate = useNavigate();
  const params = useParams<{ step: string }>();

  const currentStep = createMemo(() => {
    return params.step || routes[0];
  });

  const currentStepIndex = createMemo(() => {
    const stepIndex = routes.findIndex((route) => route === currentStep());
    return stepIndex >= 0 ? stepIndex + 1 : 1;
  });

  const goToStep = (
    stepRoute: OnboardingV3Routes,
    options?: {
      queryParams?: Record<string, string>;
    }
  ) => {
    const query = new URLSearchParams(options?.queryParams).toString();
    navigate(`/onboarding/${stepRoute}${query ? `?${query}` : ''}`);
  };

  const next = async () => {
    const currentIndex = currentStepIndex();

    if (currentIndex === totalSteps) {
      await completeOnboarding();
      navigate('/');
      return;
    }

    const nextRoute = onboardingV3StepsDict.getOptions()[currentIndex].meta?.path as OnboardingV3Routes;
    const nextStatus = onboardingV3StepsDict.getOptions()[currentIndex].value as OnboardingV3Status;

    if (nextStatus) {
      updateOnboardingStep(nextStatus);
    }

    goToStep(nextRoute);
  };

  const prev = () => {
    const currentIndex = currentStepIndex();
    if (currentIndex > 1) {
      const prevRoute = onboardingV3StepsDict.getOptions()[currentIndex - 2].meta?.path as OnboardingV3Routes;
      const prevStatus = onboardingV3StepsDict.getOptions()[currentIndex - 2].value as OnboardingV3Status;
      if (prevStatus) {
        updateOnboardingStep(prevStatus);
      }
      goToStep(prevRoute);
    }
  };

  // Auto navigation logic moved from utils.ts
  const navigationToStep = () => {
    if (companies.loading) return;

    const _companies = companies();
    if (!_companies) return;

    const { onboarded, onboardingStatus } = _companies;

    if (onboarded || onboardingStatus === OnboardingStatus.PAID) {
      const lastStep = onboardingV3StepsDict.getOptions().at(-1)?.meta?.path as OnboardingV3Routes;
      return navigate(`/onboarding/${lastStep}`);
    }

    if (onboardingStatus === OnboardingStatus.USER_HIDE_ONBOARDING) {
      return navigate('/');
    }

    const currentRoute = onboardingV3StepsDict.getMetaByValue(onboardingStatus as OnboardingV3Status)?.path as OnboardingV3Routes;

    if (currentRoute) {
      return navigate(`/onboarding/${currentRoute}`);
    }

    return navigate(`/onboarding/${OnboardingV3Routes.ADD_PROPERTIES_AND_UNITS}`);
  };

  return {
    currentStep,
    currentStepIndex,
    totalSteps,
    goToStep,
    next,
    prev,
    OnboardingV3Routes,
    navigationToStep,
  };
});
